/**
 * @fileoverview Lightweight hook for the sidebar badge: returns the number of
 * pending (not done) student tasks. Defensive: fetches only when enabled and
 * degrades to 0 on any error (no white screen, no thrown error in the sidebar).
 * Refetches whenever the spine changes (see events.ts) so the badge is live.
 */

import { useEffect, useState } from 'react'
import { dataSource } from '../dataSource'
import { subscribeStudentTasksUpdated } from '../events'

/**
 * Counts the student's pending (open) tasks for the "My tasks" sidebar badge.
 * @param enabled Only fetches when true (Arc student); otherwise returns 0.
 */
export function useStudentTasksCount(enabled: boolean): number {
  const [count, setCount] = useState(0)
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(
    () => subscribeStudentTasksUpdated(() => setReloadToken(token => token + 1)),
    [],
  )

  useEffect(() => {
    if (!enabled) {
      setCount(0)
      return
    }
    let cancelled = false
    dataSource
      .fetchTasks()
      .then(tasks => {
        if (!cancelled) setCount(tasks.filter(task => !task.done).length)
      })
      .catch(() => {
        if (!cancelled) setCount(0)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, reloadToken])

  return count
}
